<script>
import { mapGetters } from 'vuex';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';
import { sanitizeVariableSearchKey } from 'dashboard/helper/commons';
import MentionBox from '../mentions/MentionBox.vue';

const TICKET_STATIC_VARIABLES = [
  { key: 'ticket.id',          label: 'Ticket ID' },
  { key: 'ticket.subject',     label: 'Ticket Subject' },
  { key: 'ticket.description', label: 'Ticket Description' },
  { key: 'ticket.status',      label: 'Ticket Status' },
  { key: 'ticket.priority',    label: 'Ticket Priority' },
  { key: 'ticket.due_date',    label: 'Ticket Due Date' },
  { key: 'ticket.url',         label: 'Ticket URL' },
  { key: 'ticket.contact.name',  label: 'Ticket Contact Name' },
  { key: 'ticket.contact.email', label: 'Ticket Contact Email' },
  { key: 'ticket.contact.phone', label: 'Ticket Contact Phone' },
  { key: 'ticket.company.name',   label: 'Ticket Company Name' },
  { key: 'ticket.company.domain', label: 'Ticket Company Domain' },
  { key: 'ticket.agent.name',    label: 'Ticket Agent Name' },
  { key: 'ticket.agent.email',   label: 'Ticket Agent Email' },
  { key: 'ticket.tags',                     label: 'Ticket Tags' },
  { key: 'ticket.dawatech_version',         label: 'Dawatech Version' },
  { key: 'ticket.company.dawatech_version', label: 'Dawatech Version (alt)' },
  { key: 'ticket.cf_training_type',         label: 'Training Type (L1)' },
  { key: 'ticket.cf_location',              label: 'Training Location (L2)' },
  { key: 'ticket.cf_training_for',          label: 'Training For (L3)' },
  { key: 'ticket.cf_support_name',          label: 'Support Request Name (L3)' },
  { key: 'ticket.request_type',     label: 'Request Type (L1)' },
  { key: 'ticket.request_on',       label: 'Request On (L2)' },
  { key: 'ticket.request_name',     label: 'Request Name (L3)' },
];

export default {
  components: { MentionBox },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  emits: ['selectVariable'],
  computed: {
    ...mapGetters({
      customAttributes: 'attributes/getAttributes',
      ticketFieldSettings: 'ticketFieldSettings/getTicketFieldSettings',
    }),
    sanitizedSearchKey() {
      return sanitizeVariableSearchKey(this.searchKey);
    },
    items() {
      const all = [
        ...this.standardAttributeVariables,
        ...this.customAttributeVariables,
        ...this.ticketStaticVariables,
        ...this.ticketCustomFieldVariables,
        ...this.contactCustomAttributeVariables,
      ];
      if (!this.sanitizedSearchKey) return all;
      return all.filter(
        v =>
          v.label.toLowerCase().includes(this.sanitizedSearchKey.toLowerCase()) ||
          v.key.toLowerCase().includes(this.sanitizedSearchKey.toLowerCase())
      );
    },
    standardAttributeVariables() {
      return MESSAGE_VARIABLES.filter(variable => {
        return (
          variable.label.includes(this.sanitizedSearchKey) ||
          variable.key.includes(this.sanitizedSearchKey)
        );
      }).map(variable => ({
        label: variable.key,
        key: variable.key,
        description: variable.label,
      }));
    },
    customAttributeVariables() {
      return this.customAttributes
        .filter(
          attr =>
            attr.attribute_model === 'conversation_attribute' ||
            attr.attribute_model === 'contact_attribute'
        )
        .map(attribute => {
          const attributePrefix =
            attribute.attribute_model === 'conversation_attribute'
              ? 'conversation'
              : 'contact';
          return {
            label: `${attributePrefix}.custom_attribute.${attribute.attribute_key}`,
            key: `${attributePrefix}.custom_attribute.${attribute.attribute_key}`,
            description: attribute.attribute_description,
          };
        });
    },
    ticketStaticVariables() {
      return TICKET_STATIC_VARIABLES.map(v => ({
        label: v.key,
        key: v.key,
        description: v.label,
      }));
    },
    ticketCustomFieldVariables() {
      if (!this.ticketFieldSettings?.length) return [];
      const vars = [];
      this.ticketFieldSettings.forEach(field => {
        const base = `ticket.cf_${field.field_key}`;
        vars.push({ label: base, key: base, description: field.label });
        if (field.field_type === 'dropdown' && field.options?.length) {
          const hasL2 = field.options.some(o => o.children?.length > 0);
          if (hasL2) {
            const l2Label = field.options.find(o => o.level2_label)?.level2_label || `${field.label} Level 2`;
            vars.push({
              label: `${base}_l2`,
              key: `${base}_l2`,
              description: l2Label,
            });
            const hasL3 = field.options.some(o =>
              o.children?.some(c => c.children?.length > 0)
            );
            if (hasL3) {
              const l3Label = field.options
                .flatMap(o => o.children || [])
                .find(c => c.level3_label)?.level3_label || `${field.label} Level 3`;
              vars.push({
                label: `${base}_l3`,
                key: `${base}_l3`,
                description: l3Label,
              });
            }
          }
        }
      });
      return vars;
    },
    contactCustomAttributeVariables() {
      return this.customAttributes
        .filter(attr => attr.attribute_model === 'contact_attribute')
        .map(attr => ({
          label: `ticket.contact.${attr.attribute_key}`,
          key: `ticket.contact.${attr.attribute_key}`,
          description: `Ticket Contact: ${attr.attribute_display_name || attr.attribute_key}`,
        }));
    },
  },
  methods: {
    handleVariableClick(item = {}) {
      this.$emit('selectVariable', item.key);
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <MentionBox
    v-if="items.length"
    type="variable"
    :items="items"
    @mention-select="handleVariableClick"
  />
</template>

<style scoped>
.variable--list-label {
  font-weight: 600;
}
</style>
